import { ACTIVE_STATES, MAX_RUN_MS, failureAdvice, jobState, progressMessage, resultExpiry } from '@config/queueConfig'
import useTicker from '@hooks/useTicker'
import { STATE_ICONS } from './stateIcons'
import { fmtDuration, fmtElapsed } from './queueFormat'
import { expiryNote } from './resultRetention'

/**
 * The status column for a queue row — ONE component for every state, so a badge,
 * colour or label can never drift between states.
 *
 * Running rows carry a real progress bar; per WCAG a bar alone is not accessible,
 * so it has role="progressbar" + aria-valuenow AND a text equivalent beside it.
 *
 * This cell owns its OWN clock rather than taking `now` as a prop. A prop-driven
 * clock forced the parent's `columns` memo to change identity every second, which
 * remounted each row's kebab menu (see useTicker for the full story). Only a
 * running row actually counts, so only a running row ticks.
 */
const JobStatusCell = ({ job }) => {
  const state = job.effectiveState || job.state
  const vocab = jobState(state)
  const Icon = STATE_ICONS[vocab.icon] || STATE_ICONS.dots
  const active = ACTIVE_STATES.has(state)
  const percent = Math.min(100, Math.max(0, Number(job.percent) || 0))
  // Per-second only while in flight. A "ready" row still shows an expiry
  // countdown, but that reads in hours/days — a minute is plenty, and it keeps a
  // long-lived tab from freezing the countdown at its mount value.
  //
  // Spec 152 — counted to `resultExpiry`, the SAME instant `deriveState` expires the row
  // on: the earlier of the row's `expires_at` and the results bucket's age. Counting to
  // `expiresAt` alone promised an ad-hoc result a week on prod, whose bucket keeps two
  // days, and said nothing at all for a schedule's run (`expires_at` null).
  //
  // The WORDS come from `expiryNote`: a live schedule's run is also pruned by its "last N"
  // count the moment N newer runs finish, so its figure is an upper bound ("up to 3d
  // left"), never a bare "expires in 3d" (spec 152 review).
  const showsExpiry = state === 'ready' && resultExpiry(job) != null
  const now = useTicker(active || showsExpiry, active ? 1000 : 60_000)

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${vocab.badge}`}>
          <Icon />
          {vocab.label}
          {active && percent > 0 ? ` ${percent}%` : ''}
        </span>

        {/* Ready rows show how long the saved result survives — the single most
            decision-relevant fact once a report has finished. */}
        {showsExpiry && (
          <span className="text-[11px] text-gray-500" data-testid="job-expiry-note">{expiryNote(job, now)}</span>
        )}
      </div>

      {active && (
        <>
          <div
            role="progressbar"
            aria-label={`${job.displayName || 'Report'} progress`}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
          >
            <div className="h-full rounded-full bg-cb-accent transition-all" style={{ width: `${percent}%` }} />
          </div>
          <p className="mt-1.5 text-[11px] text-gray-500">
            {/* Sanitised: CB writes raw SQL exceptions into the job's message,
                and a running row must not present one as its progress detail —
                it contradicts the badge and leaks internal names and ids. */}
            {progressMessage(job)}
            {fmtElapsed(job.createdAt, now) ? ` · ${fmtElapsed(job.createdAt, now)}` : ''}
          </p>
        </>
      )}

      {state === 'failed' && (
        <p className="mt-1.5 text-[11px] leading-relaxed text-red-700">
          {/* Never invent remedial advice. Where the real remedy is known (report
              22 — CC-50022 proved a narrower range makes it WORSE) we say it;
              otherwise we state only that the run stopped. */}
          {job.durationMs >= MAX_RUN_MS * 0.95
            ? `Stopped at the ${Math.round(MAX_RUN_MS / 60000)}-minute limit.`
            : `It ran for ${fmtDuration(job.durationMs)} and stopped without returning data.`}
          {failureAdvice(job.reportId) ? ` ${failureAdvice(job.reportId)}` : ''}
        </p>
      )}

      {state === 'expired' && (
        <p className="mt-1.5 text-[11px] text-gray-500">Result is no longer stored</p>
      )}
    </div>
  )
}

export default JobStatusCell
